import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { useQueryClient } from 'react-query';

import { makeStyles, useTheme } from '@mui/styles';
import {
  Avatar,
  Box,
  Button,
  FormControlLabel,
  Grid,
  InputBase,
  Link,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { PatientTabsContext } from '../../../lib/contexts';
import { AdminProgressBar } from '../../../components/AdminProgressBar';
import { postPatientNote } from '../../../lib/api/postPatientNote';
import { AdminCheckbox } from '../../../components/AdminCheckbox';
import { getSession } from '../../../lib/api/getSession';
import { toCapitalizeFromUnderScore } from '../../../lib/helpers/helpers';
import PersonIcon from '@mui/icons-material/Person';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles((theme) => ({
  summaryPatientChartCard: {
    ...theme.globalClasses.patientChartCard,
    ...theme.globalClasses.adminTableWrapper,
    padding: 0,
    margin: 0,
  },
  summaryTextArea: {
    ...theme.typography.body1,
    '& textarea': {
      height: 'auto',
      width: '100%',
      fontSize: 18,
      color: theme.palette.inputText.color,
    },
  },
  summaryTextAreaLabel: {
    color: theme.palette.admin.main,
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  summaryTextAreaButton: {
    fontSize: '15px',
    fontWeight: '700',
    lineHeight: '26px',
    letterSpacing: '0.46px',
  },
  summaryAvatar: {
    padding: 6,
    marginRight: 13,
    alignSelf: 'center',
    backgroundColor: theme.palette.admin.primaryLight,
  },
  placeholderAvatar: {
    padding: 6,
    marginRight: 13,
    alignSelf: 'center',
    backgroundColor: theme.palette.primary.primaryLight,
  },
  inputFrame: {
    ...theme.globalClasses.inputFrame,
    padding: 24,
    border: '1px solid var(--divider, rgba(0, 0, 0, 0.12))',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    color: theme.palette.inputText.color,
  },
  patientChartCard: theme.globalClasses.patientChartCard,
  textArea: {
    marginTop: 20,
    width: '100%',
    height: 230,
    '& textarea': {
      padding: 10,
      height: 120,
      width: '100%',
      color: theme.palette.inputText.color,
    },
    color: theme.palette.inputText.color,
    fontSize: 12,
  },
  options: {
    marginTop: -41,
    display: 'block',
    float: 'left',
  },
  buttons: {
    marginTop: 24,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'wrap',
  },
  dateText: {
    marginRight: 'auto',
    color: theme.palette.admin.main,
  },
}));

/**
 * This is the component for adding notes to a patient. By adding the "summary" prop, we are able to
 * use the old and new design. Old design, no summary, just shows a basic box to add, save notes, etc.
 * If the summary is true, we will show the new design on the summary screen. Once we transition to the
 * new design, we can get rid of the summary prop and just use the new design.
 *
 * Passing in setSetSubtab to get the old View All to work again.
 *
 * @param {boolean} props.summary
 * @param {function} props.setSetSubtab
 * @param {number} props.minRows - Minimum number of rows to display (default: 2)
 * @param {number} props.maxRows - Maximum number of rows before scrolling (default: 6)
 * @returns
 */
const PatientAddNotesBox = ({ summary = false, setSetSubtab, minRows = 2, maxRows = 6 }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { currentTab },
    actions: { refetchNotes },
  } = useContext(PatientTabsContext);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isAssessment, setIsAssessment] = useState(false);
  const [isConsultation, setIsConsultation] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [isUserLoading, setIsUserLoading] = useState(true);

  // Add a ref to track if component is mounted
  const isMounted = useRef(true);

  // Set up the cleanup function
  useEffect(() => {
    // When the component mounts, set isMounted to true
    isMounted.current = true;

    // When the component unmounts, set isMounted to false
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    setIsUserLoading(true);
    getSession()
      .then((data) => {
        // Only update state if component is still mounted
        if (isMounted.current) {
          setAdminUser(data);
        }
      })
      .finally(() => {
        // Only update state if component is still mounted
        if (isMounted.current) {
          setIsUserLoading(false);
        }
      });
  }, []);

  const throttleAutoSave = useCallback((event) => {
    const note = event.target.value;
    setNewNoteContent(note);
  }, []);

  const queryClient = useQueryClient();
  const handleNotePublish = useCallback(() => {
    setIsSaving(true);
    const newData = {
      title: '',
      content: newNoteContent,
      patient_id: currentTab.id,
      is_assessment: isAssessment,
      is_consultation: isConsultation,
    };
    postPatientNote(newData).then(() => {
      // Only update state if component is still mounted
      if (isMounted.current) {
        setNewNoteContent('');
        setIsSaving(false);
        refetchNotes();
        queryClient.invalidateQueries('patient_note_');
        queryClient.invalidateQueries('patient_note_' + currentTab.id);

        // Show appropriate snackbar based on whether we're in summary mode
        if (summary && setSetSubtab) {
          // In summary mode, include "Take to Note" link
          enqueueSnackbar('Success', {
            variant: 'success',
            autoHideDuration: 3000,
            action: (key) => (
              <Link
                onClick={() => {
                  setSetSubtab('6');
                }}
                style={{
                  color: '#fff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  marginLeft: '8px',
                }}
              >
                Take to Note
              </Link>
            ),
          });
        } else {
          // Not in summary mode, just show success message without link
          enqueueSnackbar('Success', {
            variant: 'success',
            autoHideDuration: 3000,
          });
        }
      }
    });
  }, [
    newNoteContent,
    currentTab,
    isConsultation,
    isAssessment,
    enqueueSnackbar,
    queryClient,
    refetchNotes,
    setSetSubtab,
    summary, // Add summary as a dependency
  ]);

  return (
    <>
      {summary ? (
        <Grid container>
          <Paper className={classes.summaryPatientChartCard}>
            <Grid
              container
              className={classes.inputFrame}
            >
              <Grid
                item
                xs={12}
              >
                <form className={classes.summaryTextArea}>
                  <InputBase
                    name="summary-text-area"
                    id="summary-text-area"
                    value={newNoteContent}
                    multiline
                    minRows={minRows}
                    maxRows={maxRows}
                    fullWidth={true}
                    onChange={throttleAutoSave}
                    placeholder={'Write a note about this patient visit...'}
                    sx={{
                      color: theme.palette.inputText.color,
                      '& .MuiInputBase-input::placeholder': {
                        color: theme.palette.inputText.color,
                        WebkitTextFillColor: theme.palette.inputText.color,
                        opacity: 1,
                      },
                      '& .MuiInputBase-input': {
                        color: theme.palette.inputText.color,
                        WebkitTextFillColor: theme.palette.inputText.color,
                        opacity: 1,
                      },
                      '& .MuiInputBase-input.Mui-disabled': {
                        color: theme.palette.inputText.color,
                        WebkitTextFillColor: theme.palette.inputText.color,
                        opacity: 1,
                      },
                      marginBottom: 0,
                    }}
                  />

                  {isSaving ? (
                    <AdminProgressBar style={{ margin: '24px auto 5px' }} />
                  ) : (
                    <>
                      <Box className={classes.buttons}>
                        <Avatar className={isUserLoading ? classes.placeholderAvatar : classes.summaryAvatar}>
                          {!isUserLoading && adminUser?.firstName && adminUser?.lastName ? (
                            toCapitalizeFromUnderScore(adminUser?.firstName)[0] +
                            toCapitalizeFromUnderScore(adminUser?.lastName)[0]
                          ) : (
                            <PersonIcon />
                          )}
                        </Avatar>
                        <Typography
                          variant="body1"
                          className={classes.dateText}
                          sx={{ color: theme.palette.inputText.color }}
                        >
                          {new Date().toLocaleDateString()}
                        </Typography>

                        <FormControlLabel
                          id="assessment-checkbox-label"
                          control={
                            <AdminCheckbox
                              id="assessment-checkbox-input"
                              name="assessment-checkbox"
                              checked={isAssessment}
                              onChange={() => setIsAssessment(!isAssessment)}
                            />
                          }
                          label={'Assessment'}
                          className={classes.summaryTextAreaLabel}
                          sx={{ color: theme.palette.admin.main }}
                        />
                        <FormControlLabel
                          id="consultation-checkbox-label"
                          control={
                            <AdminCheckbox
                              id="consultation-checkbox-input"
                              name="consultation-checkbox"
                              checked={isConsultation}
                              onChange={() => setIsConsultation(!isConsultation)}
                            />
                          }
                          label={'Consultation'}
                          className={classes.summaryTextAreaLabel}
                          sx={{ color: theme.palette.admin.main }}
                        />

                        <Tooltip title={newNoteContent.length < 10 ? 'Minimum length: 10 characters' : ''}>
                          <span>
                            <Button
                              id="publish-button"
                              name="publish-button"
                              variant="contained"
                              color="primary"
                              onClick={handleNotePublish}
                              disabled={newNoteContent.length < 10}
                              className={classes.summaryTextAreaButton}
                            >
                              Publish
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    </>
                  )}
                </form>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      ) : (
        // this is the old notes box - can be removed once we finish the new work
        <Grid
          item
          xs={12}
        >
          <Paper className={classes.patientChartCard}>
            <Grid
              item
              container
              xs={12}
              mb={-2}
            >
              <Grid
                item
                xs={10}
              >
                <Typography variant={'h2'}>Add Notes</Typography>
              </Grid>
              <Grid
                item
                xs={2}
                className="print-hide"
              >
                <Typography
                  variant={'h3'}
                  onClick={() => setSetSubtab && setSetSubtab('3')}
                >
                  View All
                </Typography>
              </Grid>
            </Grid>
            <form className={classes.textArea}>
              <InputBase
                name="add-notes-text-area"
                id="add-notes-text-area"
                value={newNoteContent}
                multiline
                minRows={minRows}
                maxRows={maxRows}
                fullWidth={true}
                onChange={throttleAutoSave}
              />

              {isSaving ? (
                <AdminProgressBar style={{ margin: '-10px auto 5px' }} />
              ) : (
                <>
                  {newNoteContent && (
                    <>
                      <Box className={classes.buttons}>
                        <br />
                        <FormControlLabel
                          id="assessment-checkbox-label-alt"
                          control={
                            <AdminCheckbox
                              id="assessment-checkbox-input-alt"
                              name="assessment-checkbox-alt"
                              checked={isAssessment}
                              color={'primary'}
                              onChange={() => setIsAssessment(!isAssessment)}
                            />
                          }
                          label={'Assessment?'}
                        />
                        <FormControlLabel
                          id="consultation-checkbox-label-alt"
                          control={
                            <AdminCheckbox
                              id="consultation-checkbox-input-alt"
                              name="consultation-checkbox-alt"
                              checked={isConsultation}
                              color={'primary'}
                              onChange={() => setIsConsultation(!isConsultation)}
                            />
                          }
                          label={'Consultation?'}
                        />

                        <Button
                          id="cancel-button"
                          name="cancel-button"
                          variant={'text'}
                          onClick={() => setNewNoteContent('')}
                        >
                          Cancel
                        </Button>
                        <Tooltip title={newNoteContent.length < 10 ? 'Minimum length: 10 characters' : ''}>
                          <span>
                            <Button
                              id="publish-button"
                              name="publish-button"
                              variant={'text'}
                              onClick={handleNotePublish}
                              disabled={newNoteContent.length < 10}
                            >
                              Publish
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    </>
                  )}
                </>
              )}
            </form>
          </Paper>
        </Grid>
      )}
    </>
  );
};
export default PatientAddNotesBox;
